/**
 * The Component column's cell model, as a pure function.
 *
 * Vendor-neutral by construction: the names come from the issue's
 * `affected_components`, which ODD carries for every vendor and which the
 * marking engine canonicalises server-side. Nothing here knows what a "BIG-IP"
 * is, and nothing may be added that does — see docs/plan-inventory-vendor-guide.md.
 */

/** Names shown inline before the rest collapse into a "+N" affordance. */
export const MAX_INLINE_COMPONENTS = 2;

const clean = names =>
  (Array.isArray(names) ? names : []).filter(n => typeof n === 'string' && n.trim()).map(n => n.trim());

/**
 * Build the cell, or null when the issue carries no component data.
 *
 * Null is a real and expected case — not every issue in every vendor's feed is
 * attributed to a module (16 of 192 CVEs on the F5 version this was built
 * against), and those rows must render an honest em dash rather than an empty
 * cell that reads as a rendering bug.
 *
 * @param {string[]} components         the issue's affected_components
 * @param {object}   opts
 * @param {boolean}  opts.isAuto            the overlay auto-marked this issue
 * @param {object}   opts.hint              partial-enablement hint, if any
 * @param {number}   opts.configuredCiCount devices that reported any config
 */
export function componentCellData(components, { isAuto, hint, configuredCiCount } = {}) {
  const names = clean(components);
  if (!names.length) return null;

  const inline = names.slice(0, MAX_INLINE_COMPONENTS);

  return {
    names,
    inline,
    overflow: names.length - inline.length,
    allLabel: names.join(', '),
    note: buildNote({ names, isAuto, hint, configuredCiCount })
  };
}

/**
 * The muted second line, or null when there is nothing to say.
 *
 * Deliberately silent for the ordinary case. A note under every row would turn
 * the column into a wall of text and bury the two states that matter: the
 * automation acted, or the automation nearly acted and is saying why it did not.
 */
function buildNote({ names, isAuto, hint, configuredCiCount }) {
  if (isAuto) return 'no device runs this';
  if (!hint || typeof hint.enabledCiCount !== 'number') return null;

  // The denominator is configuredCiCount — devices that reported any module
  // configuration — never the fleet total, which would understate every
  // component whenever some devices report nothing. When the overlay omits it,
  // state the numerator alone rather than inventing a denominator.
  const hasDenominator = typeof configuredCiCount === 'number' && configuredCiCount > 0;
  const devices = hasDenominator
    ? `${hint.enabledCiCount} of ${configuredCiCount} devices`
    : `${hint.enabledCiCount} device${hint.enabledCiCount === 1 ? '' : 's'}`;

  // With one component the subject is unambiguous, so naming it again under
  // its own name is noise. With several, the count belongs to exactly one of
  // them and must say which.
  return names.length === 1 ? `on ${devices}` : `${hint.component} on ${devices}`;
}
